import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tooltip from './Tooltip';
import './SliderButton.css';

const SliderButton = ({
    value,
    onChange,
    // Optional: called once with the final value on release (pointer up, key up, blur).
    onCommit,
    min = 0,
    max = 100,
    step = 1,
    disabled = false,
    label,
    tooltipContent,
    className = '',
    visualMin,
    visualMax,
    children,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const effectiveVisualMin = typeof visualMin === 'number' ? visualMin : min;
    const safeMin = Number.isFinite(min) ? min : 0;
    const safeMax = Number.isFinite(max) ? max : safeMin;
    const effectiveVisualMax = typeof visualMax === 'number' ? visualMax : safeMax;
    const safeValueRaw = Number.isFinite(value) ? value : safeMin;
    const clampedValue = Math.min(safeMax, Math.max(safeMin, safeValueRaw));
    const fillPercent =
        effectiveVisualMax > effectiveVisualMin
            ? ((clampedValue - effectiveVisualMin) / (effectiveVisualMax - effectiveVisualMin)) * 100
            : 0;

    // Last value dragged to but not yet handed to onCommit.
    const pendingRef = useRef(null);

    const handleChange = useCallback(
        (e) => {
            if (disabled) return;
            const raw = parseInt(e.target.value, 10);
            const next = Number.isFinite(raw) ? raw : safeMin;
            const clamped = Math.min(safeMax, Math.max(safeMin, next));
            pendingRef.current = clamped;
            onChange?.(clamped);
        },
        [onChange, disabled, safeMin, safeMax]
    );

    const commitPending = useCallback(() => {
        if (pendingRef.current === null) return;
        const committed = pendingRef.current;
        pendingRef.current = null;
        onCommit?.(committed);
    }, [onCommit]);

    const endDrag = useCallback(() => {
        setIsDragging(false);
        commitPending();
    }, [commitPending]);
    const startDrag = useCallback(() => {
        if (!disabled) setIsDragging(true);
    }, [disabled]);

    useEffect(() => {
        if (!isDragging) return undefined;
        window.addEventListener('pointerup', endDrag);
        window.addEventListener('pointercancel', endDrag);
        return () => {
            window.removeEventListener('pointerup', endDrag);
            window.removeEventListener('pointercancel', endDrag);
        };
    }, [isDragging, endDrag]);

    const rootClassName = [
        'slider-button',
        disabled ? 'slider-button--disabled' : '',
        isDragging ? 'slider-button--dragging' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const content = (
        <div className={rootClassName}>
            <div className="slider-button__inner">
                <div
                    className="slider-button__gauge"
                    style={{ width: `${fillPercent}%` }}
                    aria-hidden
                />
                <input
                    type="range"
                    min={safeMin}
                    max={safeMax}
                    step={step}
                    value={clampedValue}
                    onChange={handleChange}
                    onPointerDown={startDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onKeyUp={commitPending}
                    onBlur={commitPending}
                    className="slider-button__slider"
                    disabled={disabled}
                    aria-label={label}
                />
                <div className="slider-button__content">
                    {children}
                </div>
            </div>
        </div>
    );

    const contentForTooltip = tooltipContent || label;

    if (!contentForTooltip) {
        return content;
    }

    return (
        <Tooltip content={contentForTooltip}>
            {content}
        </Tooltip>
    );
};

export default SliderButton;

